import { useCallback, useEffect, useRef, useState } from 'react';
import type { CuentaCobro, ImpactoDesconexion } from '../../../api/ops/pagos';
import { ErrorApi } from '../../../api/problema';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Campo } from '../../../componentes/Campo';
import { Chip, type EstadoChip } from '../../../componentes/Chip';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../../componentes/EstadoPagina';
import { Icono } from '../../../componentes/Icono';
import type { NombreIcono } from '../../../componentes/iconos';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { Ventana } from '../../../componentes/Ventana';
import { rolesDe } from '../../../logica/acceso';
import { avisoBreveDeError, estadoDeError } from '../../../logica/estados';
import { diaMes } from '../../../logica/fechasCortas';
import { navegador } from '../../../logica/navegador';
import { useCabecera, type AccionCabecera } from '../../../marco/cabecera';
import { usePermisos } from '../../../permisos/permisos';
import type { PropsPantalla } from '../../tipos';
import { AYUDA, PERMISOS, tipoCobrable, textoDeError } from '../textos';

/**
 * Cuenta de cobro (4.1): lo que se puede hacer en grande («Puedes cobrar online», «Falta terminar el alta», «Stripe ha
 * parado los cobros», «Cuenta desconectada»), los datos de la cuenta y lo que pide Stripe con el texto de la API (G277).
 *
 * - Conectar, seguir el alta y desconectar, solo con `pagos.cuenta.conectar` (el propietario) y nunca con un acceso de
 *   plataforma abierto (D-059). Conectar no se puede en solo lectura; pedir otro enlace y desconectar sí (como la API).
 * - Vuelta de Stripe (el contrato de `connectPaymentAccount`): `?onboarding=return` solo vuelve a leer la cuenta (el estado
 *   lo fija el aviso de Stripe); `?onboarding=refresh` (enlace caducado) pide otro enlace y vuelve a Stripe.
 * - Desconectar: «Revisar el impacto y confirmar» con `getPaymentAccountDisconnectionImpact`, motivo obligatorio, ventana
 *   y `If-Match`; con `409 commitments_pending` se repite con `confirm_impact`. Sin «Deshacer» (G159).
 */
const PAISES: Readonly<Record<string, string>> = { ES: 'España', PT: 'Portugal', FR: 'Francia', IT: 'Italia', DE: 'Alemania', GB: 'Reino Unido', MX: 'México', AR: 'Argentina', CO: 'Colombia', CL: 'Chile' };
const MONEDAS: Readonly<Record<string, string>> = { EUR: 'euro', GBP: 'libra', USD: 'dólar', MXN: 'peso mexicano' };

const mayusculaInicial = (t: string) => t.charAt(0).toLocaleUpperCase('es') + t.slice(1);

function paisYMoneda(pais: string | undefined, moneda: string | undefined): string {
    if (pais === undefined || moneda === undefined) {
        return '';
    }
    const m = moneda.toUpperCase();
    return `${PAISES[pais.toUpperCase()] ?? pais} · ${MONEDAS[m] ?? m} (${m})`;
}

interface Hero {
    icono: NombreIcono;
    titulo: string;
    rojo?: string;
    texto: string;
    chip?: readonly [string, EstadoChip];
}

function heroDe(c: CuentaCobro): Hero {
    switch (c.status) {
        case 'active':
            return { icono: 'landmark', titulo: 'Puedes ', rojo: 'cobrar online', texto: 'Señales, garantías y entradas se cobran en tu cuenta de Stripe, y Stripe te lo transfiere al banco.' };
        case 'pending':
            return { icono: 'hourglass', titulo: 'Falta terminar el alta', texto: 'Stripe necesita unos datos más antes de dejarte cobrar. Lo que falta está aquí debajo.', chip: ['Pendiente', 'pending'] };
        case 'restricted':
            return {
                icono: 'triangle-alert',
                titulo: 'Stripe ha parado los cobros',
                texto: `${c.requirements_deadline == null ? 'Stripe pide un dato más.' : `Stripe pide un dato más antes del ${diaMes(c.requirements_deadline)}.`} Hasta entonces la venta online con pago está en pausa.`,
                chip: ['Restringida', 'problem'],
            };
        case 'disconnected':
            return { icono: 'unplug', titulo: 'Cuenta desconectada', texto: `${c.disconnected_at == null ? '' : `Desde el ${diaMes(c.disconnected_at)} `}Reservaria ya no cobra en ella. La cuenta sigue siendo tuya en Stripe, con su saldo.`, chip: ['Desconectada', 'neutral'] };
    }
}

function metaDe(c: CuentaCobro): string {
    const cuando = c.status === 'disconnected' ? `desconectada el ${diaMes(c.disconnected_at)}` : c.status === 'pending' ? `alta empezada el ${diaMes(c.created_at)}` : `conectada el ${diaMes(c.connected_at ?? c.created_at)}`;
    return `Cuenta de cobro · Stripe · ${cuando}`;
}

const DESPUES: readonly (readonly [NombreIcono, string])[] = [
    ['circle-x', 'La web, el widget y el chat dejan de cobrar online.'],
    ['shield-check', 'Tus políticas quedan suspendidas, no borradas.'],
    ['landmark', 'La cuenta sigue siendo tuya en Stripe, con su saldo.'],
    ['history', 'El historial de pagos se conserva.'],
];

export function Cuenta(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, membresia, nombreNegocio, navegar, busqueda, accesoPlataforma = false } = props;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const ver = permisos.puede(PERMISOS.cuentaVer);
    const propietario = permisos.puede(PERMISOS.cuentaConectar) && !accesoPlataforma;
    const puedeConectar = propietario && !soloLectura;
    const vuelta = busqueda.get('onboarding');

    const [cuenta, setCuenta] = useState<{ datos: CuentaCobro | null; version: string | null } | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [pais, setPais] = useState<{ pais: string; moneda: string } | null>(null);
    const [intento, setIntento] = useState(0);
    const [ventana, setVentana] = useState<'conectar' | 'desconectar' | null>(null);
    const [panel, setPanel] = useState(false);
    const [impacto, setImpacto] = useState<ImpactoDesconexion | null>(null);
    const [motivo, setMotivo] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [abriendo, setAbriendo] = useState(false);
    const [enlaceCaducado, setEnlaceCaducado] = useState<'pidiendo' | 'fallo' | null>(vuelta === 'refresh' ? (propietario ? 'pidiendo' : 'fallo') : null);
    const [hecho, setHecho] = useState<string | null>(null);
    const refrescoPedido = useRef(false);

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        if (!ver) {
            return;
        }
        let vivo = true;
        ops.getPaymentAccount(negocio)
            .then((r) => {
                if (vivo) {
                    setCuenta({ datos: r.datos, version: r.version });
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (!vivo) {
                    return;
                }
                if (e instanceof ErrorApi && e.estado === 404) {
                    setCuenta({ datos: null, version: null });
                    setError(null);
                } else {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, ver, intento]);

    // País y moneda del negocio (los de su alta): para la ventana de conectar y la cuenta sin conectar.
    useEffect(() => {
        if (!ver) {
            return;
        }
        let vivo = true;
        ops.getBusinessConVersion(negocio)
            .then((r) => {
                if (vivo) {
                    setPais({ pais: r.datos.country, moneda: r.datos.currency });
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, ver]);

    const irAStripe = useCallback(
        (pedir: () => Promise<{ url: string }>, alFallar?: () => void) => {
            setAbriendo(true);
            pedir()
                .then((enlace) => {
                    navegador.ir(enlace.url);
                })
                .catch((e: unknown) => {
                    setAbriendo(false);
                    alFallar?.();
                    avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? textoDeError(e, 'No he podido abrir Stripe. Prueba de nuevo.') });
                });
        },
        [avisar],
    );

    // Enlace caducado (`?onboarding=refresh`): se pide otro y se vuelve a Stripe sin más.
    useEffect(() => {
        if (vuelta !== 'refresh' || refrescoPedido.current) {
            return;
        }
        refrescoPedido.current = true;
        if (!propietario) {
            return;
        }
        ops.createPaymentAccountOnboardingLink(negocio)
            .then((enlace) => {
                navegador.ir(enlace.url);
            })
            .catch((e: unknown) => {
                setEnlaceCaducado('fallo');
                avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? textoDeError(e, 'No he podido pedir otro enlace. Prueba de nuevo.') });
            });
    }, [vuelta, propietario, ops, negocio, avisar]);

    const otroEnlace = () => {
        irAStripe(() => ops.createPaymentAccountOnboardingLink(negocio));
    };
    const conectar = () => {
        irAStripe(
            () => ops.connectPaymentAccount(negocio).then((r) => r.onboarding_link),
            () => {
                setVentana(null);
            },
        );
    };

    const abrirDesconectar = () => {
        setMotivo('');
        setIntentado(false);
        setImpacto(null);
        setPanel(true);
        ops.getPaymentAccountDisconnectionImpact(negocio)
            .then(setImpacto)
            .catch((e: unknown) => {
                setPanel(false);
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido calcular el impacto. Prueba de nuevo.') });
            });
    };

    const desconectar = (confirmar = false) => {
        setOcupado(true);
        ops.disconnectPaymentAccount(negocio, motivo.trim(), confirmar, cuenta?.version ?? '')
            .then(() => {
                setOcupado(false);
                setVentana(null);
                setPanel(false);
                setHecho('Cuenta desconectada. Los pagos en curso han caducado y tus políticas quedan suspendidas hasta que conectes otra cuenta.');
                recargar();
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.codigo === 'commitments_pending' && !confirmar) {
                    desconectar(true);
                    return;
                }
                setOcupado(false);
                setVentana(null);
                if (e instanceof ErrorApi && e.codigo === 'version_conflict') {
                    recargar();
                }
                avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? textoDeError(e, 'No he podido desconectar la cuenta. Prueba de nuevo.') });
            });
    };

    const c = cuenta?.datos ?? null;
    const acciones: AccionCabecera[] = [];
    if (ver && cuenta !== null && (c === null || c.status === 'disconnected') && puedeConectar) {
        acciones.push({ clave: 'conectar', texto: 'Conectar con Stripe', icono: 'plug', onClick: () => { setVentana('conectar'); } });
    }
    useCabecera({ ayuda: ver ? AYUDA.cuenta : [], acciones });

    if (!ver) {
        return <EstadoSinPermiso seccion="Cuenta de cobro" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }
    if (error !== null && cuenta === null) {
        const e = estadoDeError(error);
        return e.tipo === 'sin-permiso' ? (
            <EstadoSinPermiso seccion="Cuenta de cobro" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />
        ) : (
            <EstadoError seccion="la cuenta de cobro" codigoSoporte={e.codigoSoporte} onReintentar={recargar} movil={movil} />
        );
    }
    if (cuenta === null) {
        return <EstadoCargando seccion="la cuenta de cobro" />;
    }

    const pm = paisYMoneda(c?.country ?? pais?.pais, c?.default_currency ?? pais?.moneda);
    const nota = !propietario ? (accesoPlataforma ? 'Con un acceso de plataforma abierto no se conecta ni se desconecta la cuenta de cobro.' : 'Con tu rol ves la cuenta y lo que pide Stripe. Conectarla, completar el alta o desconectarla lo hace el propietario.') : soloLectura && (c === null || c.status === 'disconnected') ? 'El negocio está en solo lectura: no se puede conectar la cuenta ahora.' : null;

    const banda = (() => {
        if (enlaceCaducado !== null) {
            return (
                <div className="rv-pg-banda rv-pg-banda--error" role="alert">
                    <span className="rv-pg-banda__baldosa"><Icono nombre="triangle-alert" /></span>
                    <span className="rv-pg-banda__texto">
                        <strong>El enlace de Stripe ha caducado</strong>
                        <span>{enlaceCaducado === 'pidiendo' ? 'Pidiendo otro enlace…' : 'Los enlaces de alta son de un solo uso. Pide otro: no se crea otra cuenta.'}</span>
                    </span>
                    {enlaceCaducado === 'fallo' && propietario && (
                        <button type="button" className="rv-b rv-b-sec" disabled={abriendo} onClick={otroEnlace}>
                            <Icono nombre={abriendo ? 'loader-circle' : 'refresh-cw'} className={abriendo ? 'rv-gira' : undefined} />
                            <span>{abriendo ? 'Abriendo Stripe…' : 'Pedir otro enlace'}</span>
                        </button>
                    )}
                </div>
            );
        }
        if (vuelta === 'return' && c !== null) {
            const completa = c.status === 'active';
            return (
                <div className={`rv-pg-banda${completa ? '' : ' rv-pg-banda--aviso'}`} role="status">
                    <span className="rv-pg-banda__baldosa"><Icono nombre={completa ? 'check' : 'hourglass'} /></span>
                    <span className="rv-pg-banda__texto">
                        <strong>{completa ? 'Has vuelto de Stripe: alta completada' : 'Has vuelto de Stripe, pero falta algo'}</strong>
                        <span>{completa ? 'Tu cuenta ya puede cobrar. Las políticas de cobro y la venta con importe están disponibles.' : 'Puedes seguir donde lo dejaste. Te lo recordamos en la campana.'}</span>
                    </span>
                </div>
            );
        }
        return null;
    })();

    const ventanaConectar = (
        <Ventana
            abierta={ventana === 'conectar'}
            titulo="Conectar con Stripe"
            texto="Te llevamos a Stripe para darte de alta: tu identidad y la cuenta del banco. Reservaria no ve esos datos."
            accion="Ir a Stripe"
            accionIcono="external-link"
            ocupado={abriendo}
            ocupadoTexto="Abriendo Stripe…"
            movil={movil}
            onAccion={conectar}
            onCerrar={() => { setVentana(null); }}
        >
            <div className="rv-ca-ventana-lineas">
                <div className="rv-ca-ventana-linea"><span>País</span><strong>{PAISES[(pais?.pais ?? '').toUpperCase()] ?? pais?.pais ?? ''}</strong></div>
                <div className="rv-ca-ventana-linea"><span>Moneda</span><strong>{pais === null ? '' : `${mayusculaInicial(MONEDAS[pais.moneda.toUpperCase()] ?? pais.moneda)} (${pais.moneda.toUpperCase()})`}</strong></div>
                <div className="rv-ca-ventana-linea"><span>Tipo de cuenta</span><strong>Express</strong></div>
                <div className="rv-ca-ventana-linea"><span>En Stripe</span><strong>Identidad y cuenta bancaria</strong></div>
            </div>
            <p className="rv-ca-nota rv-ca-nota--gris"><Icono nombre="info" /><span>Stripe no deja cambiar el país después: para otro país hace falta otra cuenta.</span></p>
        </Ventana>
    );

    if (c === null) {
        return (
            <div className="rv-ca-apartado">
                {banda}
                <section className="rv-pg-conectar">
                    <div className="rv-pg-conectar__izq">
                        <span className="rv-pg-baldosa rv-pg-baldosa--roja"><Icono nombre="plug" /></span>
                        <p className="rv-ca-ceja">Cuenta de cobro · Stripe</p>
                        <h2>Conecta tu cuenta para cobrar online</h2>
                        <p>Señales, tarjetas de garantía y entradas. El dinero va directo a tu cuenta de Stripe; Reservaria no lo toca.</p>
                        {puedeConectar && (
                            <span>
                                <button type="button" className="rv-b rv-b-pri" onClick={() => { setVentana('conectar'); }}>
                                    <Icono nombre="plug" />
                                    <span>Conectar con Stripe</span>
                                </button>
                            </span>
                        )}
                        {nota !== null && <p className="rv-ca-nota"><Icono nombre="eye" /><span>{nota}</span></p>}
                    </div>
                    <div className="rv-pg-conectar__der">
                        {(
                            [
                                ['external-link', 'Alta en Stripe', 'Tu identidad y la cuenta del banco, en las páginas de Stripe. Unos minutos.'],
                                ['shield-check', 'Tus datos, en Stripe', 'Reservaria nunca ve ni guarda tus datos bancarios.'],
                                ...(pm === '' ? [] : [['globe', pm, 'El país y la moneda de tu negocio, los de su alta.']]),
                            ] as [NombreIcono, string, string][]
                        ).map(([icono, t, b]) => (
                            <div key={t} className="rv-pg-paso">
                                <span className="rv-pg-paso__icono"><Icono nombre={icono} /></span>
                                <span><strong>{t}</strong><span>{b}</span></span>
                            </div>
                        ))}
                    </div>
                </section>
                {ventanaConectar}
            </div>
        );
    }

    const hero = heroDe(c);
    const boton = (c.status === 'pending' || c.status === 'restricted') && propietario
        ? { texto: abriendo ? 'Abriendo Stripe…' : c.status === 'pending' ? 'Seguir en Stripe' : 'Completar en Stripe', icono: abriendo ? ('loader-circle' as const) : ('external-link' as const), accion: otroEnlace }
        : c.status === 'disconnected' && puedeConectar
          ? { texto: 'Conectar otra cuenta', icono: 'plug' as const, accion: () => { setVentana('conectar'); } }
          : null;
    const parado = (ok: boolean) => (ok ? <strong>Permitidos</strong> : <Chip estado="problem">Parados</Chip>);
    const requisitos = c.requirements_due_labels ?? (c.requirements_due ?? []).map((code) => ({ code, label: 'Stripe pide un dato más' }));
    const vivo = c.status !== 'disconnected';
    const capacidades: [boolean, NombreIcono, string, string][] = [
        [c.charges_enabled, 'banknote', 'Cobrar señales y garantías', c.charges_enabled ? 'En reservas, citas y estancias, con tus políticas de cobro.' : vivo ? 'Hasta que Stripe permita los cobros.' : 'La cuenta está desconectada.'],
        [c.charges_enabled, 'ticket', 'Vender con importe', c.charges_enabled ? 'La página de pago de Stripe: tarjeta, Apple Pay y Google Pay.' : vivo ? 'La venta online con pago está en pausa.' : 'Sin cuenta, se vende solo lo gratuito.'],
        [vivo && c.status !== 'pending', 'rotate-ccw', 'Reembolsar lo ya cobrado', vivo && c.status !== 'pending' ? 'Total o parcial, desde Pagos.' : c.status === 'pending' ? 'Todavía no hay nada cobrado.' : 'Ya no se opera en esta cuenta.'],
        [c.payouts_enabled, 'landmark', 'Recibir transferencias en tu banco', c.payouts_enabled ? 'Stripe las hace con su calendario por defecto.' : c.status === 'pending' ? 'Cuando Stripe verifique tu banco.' : 'Ya no se opera en esta cuenta.'],
    ];
    const resumenCapacidades = c.charges_enabled && c.payouts_enabled ? 'Todo: cobrar señales y garantías, vender con importe, reembolsar y recibir transferencias.' : 'Hay cosas que todavía no puedes hacer.';

    return (
        <div className="rv-ca-apartado">
            {banda}
            {hecho !== null && (
                <div className="rv-ca-hecho" role="status">
                    <span className="rv-ca-hecho__baldosa"><Icono nombre="check" /></span>
                    <span>{hecho}</span>
                </div>
            )}
            <div className="rv-pg-hero">
                <div className="rv-pg-hero__texto">
                    <span className="rv-pg-meta">{metaDe(c)}</span>
                    <h2 className="rv-pg-hero__titulo">
                        {hero.titulo}
                        {hero.rojo !== undefined && <em>{hero.rojo}</em>}
                        {hero.chip !== undefined && <Chip estado={hero.chip[1]}>{hero.chip[0]}</Chip>}
                    </h2>
                    <p>{hero.texto}</p>
                </div>
                {boton !== null && (
                    <button type="button" className="rv-b rv-b-pri" disabled={abriendo} onClick={boton.accion}>
                        <Icono nombre={boton.icono} className={abriendo ? 'rv-gira' : undefined} />
                        <span>{boton.texto}</span>
                    </button>
                )}
            </div>
            {nota !== null && <p className="rv-ca-nota"><Icono nombre="eye" /><span>{nota}</span></p>}
            {c.status === 'restricted' && (
                <div className="rv-ca-caja rv-ca-caja--error"><div className="rv-ca-caja__fila"><Icono nombre="ban" /><span>No se crean cobros nuevos: la web, el widget y el chat dejan de pedir pago y no se pueden reservar los sitios que lo exigen. Lo ya cobrado y los reembolsos siguen.</span></div></div>
            )}
            {c.status === 'disconnected' && (
                <div className="rv-ca-caja"><div className="rv-ca-caja__fila"><Icono nombre="info" /><span>Tus políticas de cobro están suspendidas, no borradas: vuelven al conectar otra cuenta. El historial de pagos se conserva.</span></div></div>
            )}
            <section className="rv-pg-datos" aria-label="Datos de la cuenta">
                {vivo && <div className="rv-pg-dato"><span>Cobros online</span>{parado(c.charges_enabled)}</div>}
                {vivo && <div className="rv-pg-dato"><span>Pagos a tu banco</span>{parado(c.payouts_enabled)}</div>}
                {c.status === 'disconnected' && <div className="rv-pg-dato"><span>Desconectada el</span><strong>{diaMes(c.disconnected_at)}</strong></div>}
                <div className="rv-pg-dato"><span>País y moneda</span><strong>{paisYMoneda(c.country, c.default_currency)}</strong></div>
                {vivo && <div className="rv-pg-dato"><span>Tipo de cuenta</span><strong>{c.account_type === 'express' ? 'Express' : 'Standard'}</strong></div>}
                <div className="rv-pg-dato"><span>{c.status === 'pending' ? 'Alta empezada el' : 'Conectada el'}</span><strong>{diaMes(c.status === 'pending' ? c.created_at : (c.connected_at ?? c.created_at))}</strong></div>
                {c.stripe_account_id !== undefined && (
                    <div className="rv-pg-dato rv-pg-dato--ancho">
                        <span>Referencia en Stripe</span>
                        <span className="rv-pg-ref">
                            <strong>{c.stripe_account_id}</strong>
                            <Copiar texto={c.stripe_account_id} />
                        </span>
                    </div>
                )}
            </section>
            {vivo && requisitos.length > 0 && (
                <section className="rv-pg-tarjeta">
                    <div className="rv-ca-bloque__cab">
                        <strong>Lo que pide Stripe</strong>
                        <span>Se completa en las páginas de Stripe. Nunca lo pedimos aquí.</span>
                    </div>
                    <div role="list">
                        {requisitos.map((rq) => (
                            <div key={rq.code} role="listitem" className="rv-pg-req">
                                <span className="rv-ca-baldosa"><Icono nombre="file-text" /></span>
                                <strong>{rq.label}</strong>
                                <Chip estado={c.status === 'restricted' ? 'problem' : 'pending'}>{c.requirements_deadline == null ? 'Pendiente' : `Antes del ${diaMes(c.requirements_deadline)}`}</Chip>
                            </div>
                        ))}
                    </div>
                </section>
            )}
            <details className="rv-pg-plegable">
                <summary>
                    <span className="rv-ca-baldosa"><Icono nombre="list-checks" /></span>
                    <span className="rv-ca-fila__texto"><strong>Qué puedes hacer con tu cuenta</strong><span>{resumenCapacidades}</span></span>
                    <Icono nombre="chevron-down" className="rv-pg-plegable__flecha" />
                </summary>
                <div className="rv-pg-capacidades">
                    {capacidades.map(([ok, icono, t, b]) => (
                        <div key={t} className={`rv-pg-capacidad${ok ? '' : ' rv-pg-capacidad--no'}`}>
                            <Icono nombre={ok ? icono : 'circle-x'} />
                            <span><strong>{t}</strong><span>{b}</span></span>
                        </div>
                    ))}
                </div>
                {propietario && vivo && (
                    <div className="rv-pg-desconectar">
                        <span>Antes de desconectar ves qué deja de cobrarse: pagos en curso, garantías guardadas y disputas abiertas.</span>
                        <button type="button" className="rv-b rv-b-sec rv-b-rojo" onClick={abrirDesconectar}>
                            <Icono nombre="unplug" />
                            <span>Desconectar la cuenta</span>
                        </button>
                    </div>
                )}
            </details>

            <PanelLateral
                abierto={panel}
                onCerrar={() => { setPanel(false); }}
                movil={movil}
                titulo="Desconectar la cuenta de cobro"
                eyebrow="Revisar el impacto y confirmar"
                subtitulo={c.stripe_account_id === undefined ? 'Stripe' : `Stripe · ${c.stripe_account_id}`}
                icono="unplug"
                accion="Desconectar"
                accionIcono="unplug"
                inactiva={impacto === null || !impacto.can_disconnect}
                onAccion={() => {
                    setIntentado(true);
                    if (motivo.trim() !== '') {
                        setVentana('desconectar');
                    }
                }}
            >
                {impacto === null ? (
                    <EstadoCargando seccion="el impacto" />
                ) : (
                    <Impacto impacto={impacto} motivo={motivo} setMotivo={setMotivo} error={intentado && motivo.trim() === '' ? 'Escribe el motivo.' : null} />
                )}
            </PanelLateral>
            <Ventana
                abierta={ventana === 'desconectar'}
                titulo="¿Desconectar la cuenta de cobro?"
                texto="Reservaria deja de cobrar en esta cuenta. Los pagos en curso caducan y tus políticas quedan suspendidas. No se puede deshacer."
                accion="Desconectar"
                accionIcono="unplug"
                ocupado={ocupado}
                ocupadoTexto="Desconectando…"
                movil={movil}
                onAccion={() => { desconectar(false); }}
                onCerrar={() => { setVentana(null); }}
            />
            {ventanaConectar}
        </div>
    );
}

function Impacto({ impacto, motivo, setMotivo, error }: { impacto: ImpactoDesconexion; motivo: string; setMotivo: (v: string) => void; error: string | null }) {
    const filas: [number, string, string, 'aviso' | 'bloquea' | null][] = [
        [impacto.pending_payments, 'Pagos en curso', 'Caducarán y su plaza se libera.', impacto.pending_payments > 0 ? 'aviso' : null],
        [impacto.saved_guarantees, 'Tarjetas de garantía guardadas', 'Ya no podrás cobrar su no-show ni su cancelación tardía.', impacto.saved_guarantees > 0 ? 'aviso' : null],
        [impacto.open_refunds, 'Reembolsos en curso', impacto.open_refunds > 0 ? 'Impiden desconectar hasta que terminen.' : 'Ninguno.', impacto.open_refunds > 0 ? 'bloquea' : null],
        [impacto.open_disputes, 'Disputas abiertas', impacto.open_disputes > 0 ? 'Impiden desconectar: responde antes en Disputas.' : 'Ninguna.', impacto.open_disputes > 0 ? 'bloquea' : null],
    ];
    const afectados = impacto.affected_payables ?? [];
    return (
        <div className="rv-ca-cuerpo">
            {!impacto.can_disconnect && (
                <div className="rv-ca-caja rv-ca-caja--error" role="alert">
                    <div className="rv-ca-caja__fila"><Icono nombre="ban" /><span>Ahora no se puede desconectar: hay reembolsos en curso o disputas abiertas. Resuélvelos en Pagos y en Disputas y vuelve aquí.</span></div>
                </div>
            )}
            <div className="rv-ca-bloque">
                <p className="rv-ca-ceja">Qué deja de cobrarse</p>
                <span className="rv-ca-nota rv-ca-nota--gris">Lo que tienes ahora en Stripe a través de Reservaria.</span>
                {filas.map(([n, t, b, tipo]) => (
                    <div key={t} className="rv-pg-impacto">
                        <span className={`rv-pg-impacto__cifra${tipo === null ? '' : ` rv-pg-impacto__cifra--${tipo}`}`}>{n.toLocaleString('es-ES')}</span>
                        <span className="rv-ca-fila__texto"><strong>{t}</strong><span>{b}</span></span>
                    </div>
                ))}
            </div>
            {afectados.length > 0 && (
                <div className="rv-ca-bloque">
                    <p className="rv-ca-ceja">Lo que se ve afectado</p>
                    {afectados.map((a) => {
                        const [tipo, icono] = tipoCobrable(a.payable.type);
                        return (
                            <div key={a.payment_id} className="rv-pg-impacto">
                                <span className="rv-ca-baldosa"><Icono nombre={icono} /></span>
                                <span className="rv-ca-fila__texto"><strong>{tipo}</strong><span>{a.payable.id}</span></span>
                                <Chip estado="neutral">{a.concept === 'guarantee' ? 'Garantía' : a.concept === 'deposit' ? 'Señal' : 'Pago'}</Chip>
                            </div>
                        );
                    })}
                </div>
            )}
            <div className="rv-ca-bloque">
                <p className="rv-ca-ceja">Después de desconectar</p>
                {DESPUES.map(([icono, t]) => (
                    <p key={t} className="rv-ca-nota"><Icono nombre={icono} /><span>{t}</span></p>
                ))}
            </div>
            {impacto.can_disconnect && <Campo etiqueta="Motivo" valor={motivo} onChange={setMotivo} filas={3} max={500} error={error} ayuda="Queda en Actividad." />}
        </div>
    );
}

/** «Copiar» una referencia (de Stripe), con su aviso breve. */
export function Copiar({ texto }: { texto: string }) {
    const avisar = useAvisoBreve();
    return (
        <button
            type="button"
            className="rv-b rv-b-sec rv-pg-copiar"
            onClick={() => {
                const portapapeles = navigator.clipboard as Clipboard | undefined;
                void portapapeles?.writeText(texto).then(
                    () => { avisar({ tipo: 'ok', texto: 'Copiado.' }); },
                    () => undefined,
                );
            }}
        >
            <Icono nombre="copy" />
            <span>Copiar</span>
        </button>
    );
}
